/* eslint-disable @typescript-eslint/no-unsafe-member-access -- magic needed for hook typings */
/* eslint-disable @typescript-eslint/no-explicit-any -- magic needed for hook typings */
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  type FetchQueryOptions,
  type InfiniteData,
  type InvalidateQueryFilters,
  type QueryFilters,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  type UseSuspenseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { type GraphQLError, Kind, print } from "graphql";
import { request } from "graphql-request";
import type { ExecutionResult, SubscribePayload } from "graphql-ws";
import { type SetStateAction, useEffect, useState } from "react";

import { useErrorDrawer } from "@/hooks/use-error-drawer.tsx";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import { getAuthorizationHeaders } from "@/lib/authentication-headers";
import type { WebSocketClient } from "@/lib/interfaces/websocket-client";
import { LivanceTypedError } from "@/lib/livance-typed-error";
import { throwOnError } from "@/lib/throw-on-error";
import { HASURA_ENDPOINT } from "@/lib/utils/config";
import type { RootRouteContext } from "@/routes/-root/-root-context";

export async function queryFn<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  authClient: AuthenticationClient,
  variables?: TVariables,
  signal?: AbortSignal,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
): Promise<any> {
  const requestHeaders = await getAuthorizationHeaders(authClient);

  return await request({
    url: authClient.hasuraEndpoint,
    document,
    variables: variables ?? undefined,
    requestHeaders,
    signal,
  });
}

export async function typedQueryFn<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  authClient: AuthenticationClient,
  variables?: TVariables,
): Promise<TData> {
  const requestHeaders = await getAuthorizationHeaders(authClient);

  return request<TData>({
    url: authClient.hasuraEndpoint,
    document,
    variables: variables ?? undefined,
    requestHeaders,
  });
}

export async function queryFnWithError<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  authClient: AuthenticationClient,
  variables?: TVariables,
  signal?: AbortSignal,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
): Promise<any> {
  const requestHeaders = await getAuthorizationHeaders(authClient);

  const result = await request({
    url: authClient.hasuraEndpoint,
    document,
    variables: variables ?? undefined,
    requestHeaders,
    signal,
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition --- intentionally unnecessary check
  if (result) {
    throwOnError(result);
  }

  return result;
}

export async function publicQueryFn<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): Promise<TData> {
  return await request(HASURA_ENDPOINT, document, variables ?? undefined, {
    "x-hasura-role": "public",
  });
}

export function usePublicGraphQLMutation<
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TData extends Record<string, any> = any,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TVariables = Record<string, any>,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: UseMutationOptions<TData, GraphQLError, TVariables>,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
): UseMutationResult<TData, GraphQLError, TVariables, any> {
  return useMutation({
    ...options,
    mutationFn: (variables) => publicQueryFn(document, variables),
  });
}

/**
 * Constrói a query key canônica usada por todos os hooks/helpers de query.
 * Normaliza `variables` para `{}` quando ausente, garantindo que os caminhos
 * de leitura (useGraphQL/useSuspenseGraphQL/prefetch/fetch/infinite) e de
 * invalidação/reset produzam exatamente a mesma chave sob hashing estrutural.
 */
export function getQueryKey<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): [string, unknown] {
  const operation = document.definitions.find(
    (definition) => definition.kind === Kind.OPERATION_DEFINITION,
  );

  if (operation?.kind !== Kind.OPERATION_DEFINITION || !operation.name) {
    throw new Error(
      "getQueryKey: o documento GraphQL precisa conter uma operação nomeada.",
    );
  }

  return [operation.name.value, variables ?? {}];
}

export function useGraphQL<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: Omit<UseQueryOptions<TData, GraphQLError>, "queryKey">,
): UseQueryResult<TData, GraphQLError> {
  const routeContext = useRouteContext({ from: "__root__" });
  const { authClient } = routeContext;

  return useQuery({
    gcTime: Infinity,
    ...options,
    queryKey: getQueryKey(document, variables),
    queryFn: ({ signal }) => queryFn(document, authClient, variables, signal),
  });
}

export function useSuspenseGraphQL<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: Omit<UseQueryOptions<TData, GraphQLError>, "queryKey">,
): UseSuspenseQueryResult<TData, GraphQLError> {
  const routeContext = useRouteContext({ from: "__root__" });
  const { authClient } = routeContext;

  return useSuspenseQuery({
    gcTime: Infinity,
    ...options,
    queryKey: getQueryKey(document, variables),
    queryFn: ({ signal }) => queryFn(document, authClient, variables, signal),
  });
}

export async function fetchQuery<TData, TVariables>(
  context: RootRouteContext,
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: FetchQueryOptions<TData, GraphQLError>,
): Promise<TData> {
  const { authClient, queryClient } = context;

  return await queryClient.fetchQuery({
    ...options,
    queryKey: getQueryKey(document, variables),
    queryFn: () => queryFn(document, authClient, variables),
  });
}

export async function ensureQueryData<TData, TVariables>(
  context: RootRouteContext,
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: FetchQueryOptions<TData, GraphQLError>,
): Promise<TData> {
  const { authClient, queryClient } = context;

  return await queryClient.ensureQueryData({
    ...options,
    queryKey: getQueryKey(document, variables),
    queryFn: () => queryFn(document, authClient, variables),
  });
}

export function useGraphQLMutation<
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TData extends Record<string, any> = any,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TVariables = Record<string, any>,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: UseMutationOptions<TData, GraphQLError, TVariables>,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
): UseMutationResult<TData, GraphQLError, TVariables, any> {
  const routeContext = useRouteContext({ from: "__root__" });
  const { authClient } = routeContext;

  return useMutation({
    ...options,
    mutationFn: (variables) => queryFn(document, authClient, variables),
  });
}

interface UseMutationOptionsExtensions {
  onCloseErrorDrawer?: SetStateAction<() => void>;
}

interface ErrorHandler {
  showMessage: boolean;
}

const ERROR_HANDLERS: Record<string, ErrorHandler | undefined> = {
  ValidationError: { showMessage: true },
  WebhookValidationError: { showMessage: true },
  UnexpectedError: { showMessage: false },
};

export function useGraphQLMutationWithErrorHandler<
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TData extends Record<string, any> = any,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TVariables = Record<string, any>,
>(
  document: TypedDocumentNode<TData, TVariables>,
  options?: UseMutationOptions<TData, GraphQLError, TVariables> &
    UseMutationOptionsExtensions,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
): UseMutationResult<TData, GraphQLError, TVariables, any> {
  const routeContext = useRouteContext({ from: "__root__" });
  const { authClient } = routeContext;
  const { showErrorDrawer } = useErrorDrawer();

  return useMutation({
    ...options,
    retry: (failureCount, error) => {
      if (error instanceof LivanceTypedError) {
        return false;
      }

      if (!options?.retry) {
        return false;
      }
      if (typeof options.retry === "number") {
        return failureCount < options.retry;
      }

      return true;
    },
    mutationFn: (variables) => queryFnWithError(document, authClient, variables),
    onSuccess: (data, variables, onMutateResult, context) => {
      const actionKey = Object.keys(data)[0] as keyof TData;
      const responseData = data[actionKey];

      if (responseData.data && options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context);
      if (error instanceof LivanceTypedError) {
        const handler = ERROR_HANDLERS[error.typeName];

        const message = handler?.showMessage ? error.message : undefined;
        showErrorDrawer(message, options?.onCloseErrorDrawer);

        return;
      }

      showErrorDrawer(undefined, options?.onCloseErrorDrawer);
    },
  });
}

export function useSubscription<
  TData,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TVariables extends Record<string, any> = Record<string, any>,
  TError = unknown,
>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): SubscriptionResult<ExecutionResult<TData>, TError> {
  const { wsClient, user } = useRouteContext({ from: "__root__" });

  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- ESLint is unable to get if unmounted or not */
  const [data, setData] = useState<ExecutionResult<TData>>();
  const [error, setError] = useState<TError | null>(null);
  useEffect(() => {
    let mounted = true;
    const cancellationController = new AbortController();
    const iter = subscriptionFn<TData, TVariables>(
      wsClient,
      document,
      variables,
      cancellationController.signal,
    );

    if (user.codUsuario === 0) {
      // User is not logged in, do nothing.
      return;
    }

    void (async () => {
      try {
        while (mounted) {
          const subscriptionResult = await iter.next();

          if (mounted) {
            setData(subscriptionResult.value);
            setError(null);
          }
          if (subscriptionResult.done) {
            break;
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err as TError);
        }
      }
    })();

    return () => {
      mounted = false;
      cancellationController.abort();
    };
  }, [user.codUsuario, document, variables, wsClient]);

  return { data, error, isError: error !== null };
}

async function* subscriptionFn<TData, TVariables extends Record<string, unknown>>(
  wsClient: WebSocketClient,
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  cancellationToken?: AbortSignal,
): AsyncGenerator<ExecutionResult<TData>, undefined> {
  const subscription = wsClient.iterate<TData, TVariables>({
    query: print(document),
    variables,
  } as SubscribePayload);

  for await (const result of subscription) {
    if (cancellationToken?.aborted) {
      break;
    }
    yield result;
  }
}

interface SubscriptionResult<TData = unknown, TError = unknown> {
  data: TData | undefined;
  error: TError | null;
  isError: boolean;
}

export function prefetchQuery<TData, TVariables>(
  routeContext: RootRouteContext,
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: Omit<UseQueryOptions<TData, GraphQLError>, "queryKey">,
): void {
  const { authClient, queryClient } = routeContext;

  void queryClient.prefetchQuery({
    ...options,
    queryKey: getQueryKey(document, variables),
    queryFn: () => queryFn(document, authClient, variables),
  });
}

export function useInvalidateQuery<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  sub?: string,
): () => void {
  const routeContext = useRouteContext({ from: "__root__" });
  const { queryClient } = routeContext;

  return () => {
    const baseKey = getQueryKey(document, variables);
    // Algumas queries do root (ex.: UserQuery) são cacheadas por `sub`
    // (`[operationName, sub]`). Quando informado, usa essa mesma chave para que
    // a invalidação atinja o cache correto.
    const queryKey = sub === undefined ? baseKey : [baseKey[0], sub];
    void queryClient.invalidateQueries({ queryKey } satisfies InvalidateQueryFilters);
  };
}

export function useResetQuery<TData, TVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
): () => void {
  const routeContext = useRouteContext({ from: "__root__" });
  const { queryClient } = routeContext;

  return () => {
    const queryKey = getQueryKey(document, variables);
    void queryClient.resetQueries({ queryKey } satisfies QueryFilters);
  };
}

type MergeVariables<TVars, TPageParam> = (base: TVars, pageParam: TPageParam) => TVars;

type InferQueryKey = readonly [operationName: string, variables: unknown];

export function useInfiniteGraphQL<
  TQueryFnData,
  TError,
  TPageParam,
  TData = InfiniteData<TQueryFnData>,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  TVariables extends Record<string, any> = Record<string, unknown>,
>(
  document: TypedDocumentNode<TQueryFnData, TVariables>,
  variables: TVariables,
  options?: Omit<
    UseInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TData,
      InferQueryKey,
      TPageParam
    >,
    "queryKey" | "initialPageParam"
  > & { initialPageLength?: number },
): UseInfiniteQueryResult<TData, TError> {
  const routeContext = useRouteContext({ from: "__root__" });
  const { authClient } = routeContext;

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const defaultGetNext = (lastPage: any): TPageParam | undefined => {
    const hasNext = Boolean(lastPage?.pageInfo?.hasNextPage);
    const endCursor = lastPage?.pageInfo?.endCursor as TPageParam | undefined;
    return hasNext
      ? ({ first: variables.first as number, after: endCursor } as TPageParam)
      : undefined;
  };

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const defaultGetPrev = (firstPage: any): TPageParam | undefined => {
    const hasPrev = Boolean(firstPage?.pageInfo?.hasPreviousPage);
    const startCursor = firstPage?.pageInfo?.startCursor as TPageParam | undefined;
    return hasPrev
      ? ({ last: variables.last as number, before: startCursor } as TPageParam)
      : undefined;
  };

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const defaultMerge: MergeVariables<TVariables, any> = (base, pageParam) => {
    if (typeof pageParam === "object") {
      return { ...base, ...pageParam } as TVariables;
    }
    if (typeof pageParam === "string") {
      return { ...base, after: pageParam } as TVariables;
    }
    return base;
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- needed for pagination
  const firstValue =
    variables.first && options?.initialPageLength
      ? options.initialPageLength
      : variables.first;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- needed for pagination
  const lastValue =
    variables.last && options?.initialPageLength
      ? options.initialPageLength
      : variables.last;

  const initialPageParam = {
    after: variables.after ? (variables.after as number) : undefined,
    before: variables.before ? (variables.before as number) : undefined,
    first: firstValue ? (firstValue as number) : undefined,
    last: lastValue ? (lastValue as number) : undefined,
  } as TPageParam;

  const mergeVariables = defaultMerge;
  const getNextPageParam = options?.getNextPageParam ?? defaultGetNext;
  const getPreviousPageParam = options?.getPreviousPageParam ?? defaultGetPrev;

  return useInfiniteQuery({
    ...options,
    gcTime: options?.gcTime ?? Infinity,
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam,
    queryFn: (props) => {
      const pageParam = "pageParam" in props ? props.pageParam : undefined;
      const merged = pageParam
        ? mergeVariables(variables, pageParam as TPageParam)
        : undefined;
      return queryFn(document, authClient, merged, props.signal);
    },
    queryKey: getQueryKey(document, variables),
  });
}
